import {
    Component,
    computed,
    input,
    InputSignal,
    linkedSignal,
    Signal,
    WritableSignal,
} from '@angular/core';

import { TabsBlockSection, TabsPosition } from '@core/cms/landing.cms';
import { AccordionItem } from '@shared/models/cms.model';
import { TrustHtmlPipe } from '@shared/pipes/trust-html/trust-html.pipe';

/**
 * Renders a `ComponentSectionTabsBlock`: optional intro content plus a set of tabs whose labels
 * come from `tabsItems` (`shared.accordion-item`) and whose bodies are rich text. One tab is open at
 * a time; `tabsPosition` lays the labels above the panel (`horizontal`) or beside it (`vertical`).
 */
@Component({
    selector: 'cms-tabs-block',
    imports: [TrustHtmlPipe],
    templateUrl: './tabs-block.html',
    styleUrl: './tabs-block.scss',
})
export class CmsTabsBlock {
    public readonly section: InputSignal<TabsBlockSection> = input.required<TabsBlockSection>();

    /** `true` when the labels sit beside the content (`vertical`) rather than above it. */
    protected readonly isVertical: Signal<boolean> = computed(
        (): boolean => this.section().tabsPosition === TabsPosition.Vertical,
    );

    /** The open tab's id; defaults to the first item and resets when the section input changes. */
    protected readonly activeTabId: WritableSignal<string | null> = linkedSignal(
        (): string | null => this.section().tabsItems[0]?.id ?? null,
    );

    /** Rich-text body of the active tab (`null` when there is no active tab). */
    protected readonly activeContent: Signal<string | null> = computed((): string | null => {
        const id: string | null = this.activeTabId();

        return (
            this.section().tabsItems.find((item: AccordionItem): boolean => item.id === id)
                ?.itemContent ?? null
        );
    });

    protected selectTab(id: string): void {
        this.activeTabId.set(id);
    }
}
